import { Component, Input, inject, signal, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { UpperCasePipe } from '@angular/common';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { LanguageService } from '../../services/language.service';
import { ClientInfo } from '../../shared/types/redirect.model';
import { CookieService } from '../../services/cookie.service';
import { environment } from '../../../environments/environment';
import { buildPortalUrl } from '../../shared/deep-link';
import { LsSelectComponent } from '../ls-select/ls-select.component';
import { SelectOptionModel } from '../ls-select/models/select-option.model';
import { LsButtonComponent } from '../ls-button/ls-button.component';

@Component({
  selector: 'app-login-more-info',
  standalone: true,
  imports: [TranslocoModule, ReactiveFormsModule, UpperCasePipe, LsSelectComponent, LsButtonComponent],
  templateUrl: './login-more-info.component.html',
  styleUrls: ['./login-more-info.component.scss'],
})
export class LoginMoreInfoComponent implements OnInit {
  private readonly languageService = inject(LanguageService);
  private readonly cookieService = inject(CookieService);
  private readonly translocoService = inject(TranslocoService);
  private readonly fb = inject(FormBuilder);

  @Input() clients: ClientInfo[] = [];
  @Input() email = '';
  @Input() deepLinkPath = '';
  @Input() inviteCode = '';

  readonly submitting = signal(false);
  readonly form: FormGroup = this.fb.group({
    client: ['', [Validators.required]]
  });

  clientOptions: SelectOptionModel[] = [];
  selectedClient: SelectOptionModel = { value: '', name: '' };

  ngOnInit(): void {
    this.buildClientOptions();
  }

  private buildClientOptions(): void {
    this.clientOptions = this.clients.map(client => 
      new SelectOptionModel(
        client.clientName ?? '',
        client.clientId?.toString() ?? ''
      )
    );
  }

  private buildRedirectUrl(portalUrl: string, clientId: string): string {
    const lang = this.languageService.getCurrentLanguage();
    const cookieConsent = this.cookieService.getCookie('ls_allow_analytics');

    if (this.inviteCode) {
      let url = `${portalUrl}/register/${encodeURIComponent(this.inviteCode)}?lang=${lang}`;
      if (cookieConsent) {
        url += `&cookie_consent=${cookieConsent}`;
      }
      return url;
    }

    return buildPortalUrl(portalUrl, this.deepLinkPath, {
      username: encodeURIComponent(this.email),
      lang,
      clientid: clientId,
      cookie_consent: cookieConsent,
    });
  }

  onClientSelect(option: SelectOptionModel): void {
    this.selectedClient = option;
  }

  onSubmit(event: Event): void {
    event.preventDefault();
    this.form.markAllAsTouched();

    if (this.form.invalid || this.submitting()) {
      return;
    }

    this.submitting.set(true);

    const selectedClientId = this.selectedClient.value;
    const selectedClient = this.clients.find(
      (c) => c.clientId?.toString() === selectedClientId
    );

    if (selectedClient) {
      window.location.href = this.buildRedirectUrl(selectedClient.portalUrl, selectedClientId);
    } else {
      // Fallback to US portal
      window.location.href = this.buildRedirectUrl(environment.portalUsUrl, selectedClientId);
    }
  }
}
